import {
  Check, Inbox, MoreHorizontal, Plus, Search, Trash2, Undo2, UserPlus, X,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  DiscardGuard,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { CopyButton, Tooltip } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { AddNumbersDialog } from "@/features/add-numbers";
import { ApiError } from "@/lib/api";
import {
  useApproveOrder, useAssignNumber, useDeleteNumber, useNumbers, useOrders, useRejectOrder,
  useReleaseNumber, useTenants,
} from "@/lib/queries";
import {
  NUMBER_STATUSES, NUMBER_STATUS_LABELS, type NumberOrder, type NumberStatus, type PhoneNumber,
} from "@/lib/types";
import { formatDateTime, relativeTime } from "@/lib/utils";
import { useDebounced } from "@/lib/use-debounced";
import { OrderStatus } from "@/routes/numbers";

/**
 * The number pool — superadmin only.
 *
 * Every number the platform holds at the carrier, who has it, and the requests waiting for a
 * decision. Approving a request hands the number to the tenant and creates its routing row,
 * disabled and unrouted, for their admin to configure; rejecting returns it to the pool with
 * the reason kept on the request so the admin who asked can see why.
 */
export function InventoryPage() {
  const toast = useToast();
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("all");
  const debounced = useDebounced(search, 250);

  const { data, isPending, error, refetch } = useNumbers({
    q: debounced || undefined,
    status: status === "all" ? undefined : status,
  });
  const { data: pending } = useOrders({ status: "pending_approval" });
  const { data: tenants } = useTenants();

  const [adding, setAdding] = React.useState(false);
  const [assigning, setAssigning] = React.useState<PhoneNumber | undefined>();
  const [releasing, setReleasing] = React.useState<PhoneNumber | undefined>();
  const [retiring, setRetiring] = React.useState<PhoneNumber | undefined>();
  const [rejecting, setRejecting] = React.useState<NumberOrder | undefined>();

  const approve = useApproveOrder();
  const release = useReleaseNumber();
  const retire = useDeleteNumber();

  const filtered = Boolean(debounced) || status !== "all";
  const tenantName = (id: number | null) => tenants?.find((t) => t.id === id)?.name ?? null;

  const onApprove = async (o: NumberOrder) => {
    try {
      await approve.mutateAsync(o.id);
      toast.success("Request approved", `${o.number} is now ${o.tenant_name ?? "the tenant"}'s`);
    } catch (e) {
      toast.error("Could not approve", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <Page>
      <PageHeader
        title="Number inventory"
        description="Every number the platform holds, who has it, and the requests waiting on you."
        action={
          <Button variant="default" onClick={() => setAdding(true)}>
            <Plus />
            Add numbers
          </Button>
        }
      />

      <Card className="mb-4 overflow-hidden">
        <CardHeader
          title="Requests"
          description={
            pending?.length
              ? "Approving hands the number to the tenant; their admin then routes it."
              : "Nothing is waiting for a decision."
          }
        />
        {(pending ?? []).length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>For</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-40" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(pending ?? []).map((o) => (
                <TableRow key={o.id}>
                  <MonoCell>{o.number}</MonoCell>
                  <TableCell>{o.tenant_name ?? "—"}</TableCell>
                  <TableCell className="text-text-muted">{o.note ?? "—"}</TableCell>
                  <TableCell className="text-xs text-text-muted">
                    <Tooltip content={formatDateTime(o.requested_at)}>
                      <span>
                        {relativeTime(o.requested_at)}
                        {o.requested_by_email ? ` · ${o.requested_by_email}` : ""}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <OrderStatus order={o} />
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1.5">
                      <Button variant="outline" size="sm" onClick={() => setRejecting(o)}>
                        <X />
                        Reject
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        loading={approve.isPending && approve.variables === o.id}
                        onClick={() => void onApprove(o)}
                      >
                        <Check />
                        Approve
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card className="overflow-hidden">
        <Toolbar>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search number or notes"
            leading={<Search />}
            className="w-64"
            aria-label="Search the inventory"
          />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              {NUMBER_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {NUMBER_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setStatus("all");
              }}
            >
              Clear
            </Button>
          )}
          <span className="ml-auto text-xs text-text-muted">
            {data ? `${data.length} number${data.length === 1 ? "" : "s"}` : null}
          </span>
        </Toolbar>

        {isPending ? (
          <TableSkeleton cols={6} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load the inventory"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title={filtered ? "No numbers match those filters" : "The pool is empty"}
            description={
              filtered
                ? "Try clearing the search or the status filter."
                : "Add the numbers the platform holds at the carrier. Tenants request them from here."
            }
            action={
              !filtered ? (
                <Button variant="default" onClick={() => setAdding(true)}>
                  <Plus />
                  Add numbers
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Where</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Held by</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((n) => (
                <TableRow key={n.id}>
                  <MonoCell>
                    <span className="inline-flex items-center gap-1">
                      <span className="text-text">{n.number}</span>
                      <CopyButton value={n.number} />
                    </span>
                    {n.number_type === "tollfree" && (
                      <Badge tone="outline" size="sm" className="ml-1.5 font-sans">
                        Toll-free
                      </Badge>
                    )}
                    {!n.enabled && (
                      <Badge tone="warning" size="sm" className="ml-1.5 font-sans">
                        Disabled
                      </Badge>
                    )}
                  </MonoCell>
                  <TableCell className="text-text-muted">
                    {[n.locality, n.region].filter(Boolean).join(", ") || n.area_code || "—"}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={n.status} />
                  </TableCell>
                  <TableCell>
                    {n.tenant_id ? (
                      n.did_id ? (
                        <Link to="/numbers" className="text-accent-11 hover:underline">
                          {n.tenant_name ?? tenantName(n.tenant_id) ?? "—"}
                        </Link>
                      ) : (
                        n.tenant_name ?? tenantName(n.tenant_id) ?? "—"
                      )
                    ) : (
                      <span className="text-xs text-text-subtle">Nobody</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-64 truncate text-xs text-text-muted">{n.notes ?? "—"}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${n.number}`}>
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {n.status === "available" && (
                          <DropdownMenuItem onSelect={() => setAssigning(n)}>
                            <UserPlus />
                            Assign to a tenant
                          </DropdownMenuItem>
                        )}
                        {n.status === "assigned" && (
                          <DropdownMenuItem onSelect={() => setReleasing(n)}>
                            <Undo2 />
                            Take back into the pool
                          </DropdownMenuItem>
                        )}
                        {n.status === "pending_approval" && (
                          <DropdownMenuItem disabled>
                            <Check />
                            Decide the request above
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          destructive
                          disabled={n.status !== "available"}
                          onSelect={() => setRetiring(n)}
                        >
                          <Trash2 />
                          Retire from the platform
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <AddNumbersDialog open={adding} onOpenChange={setAdding} />

      <AssignNumberDialog
        number={assigning}
        open={Boolean(assigning)}
        onOpenChange={(open) => !open && setAssigning(undefined)}
      />

      <RejectOrderDialog
        order={rejecting}
        open={Boolean(rejecting)}
        onOpenChange={(open) => !open && setRejecting(undefined)}
      />

      <ConfirmDialog
        open={Boolean(releasing)}
        onOpenChange={(open) => !open && setReleasing(undefined)}
        title="Take this number back?"
        description={
          <>
            <span className="font-mono">{releasing?.number}</span> is taken from{" "}
            <span className="font-medium">{releasing?.tenant_name}</span>: its route is discarded
            and it stops taking calls. It returns to the pool as available.
          </>
        }
        confirmLabel="Take it back"
        destructive
        loading={release.isPending}
        onConfirm={async () => {
          if (!releasing) return;
          try {
            await release.mutateAsync(releasing.id);
            toast.success("Number released", releasing.number);
            setReleasing(undefined);
          } catch (e) {
            toast.error("Could not release", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(retiring)}
        onOpenChange={(open) => !open && setRetiring(undefined)}
        title="Retire this number?"
        description={
          <>
            <span className="font-mono">{retiring?.number}</span> is removed from the platform,
            along with the history of requests for it. Add it again if it comes back.
          </>
        }
        confirmLabel="Retire number"
        destructive
        loading={retire.isPending}
        onConfirm={async () => {
          if (!retiring) return;
          try {
            await retire.mutateAsync(retiring.id);
            toast.success("Number retired", retiring.number);
            setRetiring(undefined);
          } catch (e) {
            toast.error("Could not retire", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Page>
  );
}

function StatusBadge({ status }: { status: NumberStatus }) {
  const tone = status === "assigned" ? "success" : status === "pending_approval" ? "warning" : "neutral";
  return (
    <Badge tone={tone} size="sm">
      {NUMBER_STATUS_LABELS[status]}
    </Badge>
  );
}

/** Hand an available number to a tenant without a request. */
function AssignNumberDialog({
  number, open, onOpenChange,
}: {
  number: PhoneNumber | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const assign = useAssignNumber();
  const { data: tenants } = useTenants({ enabled: open });
  const [tenantId, setTenantId] = React.useState<number | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    setTenantId(tenants?.length === 1 ? tenants[0]!.id : null);
  }, [open, tenants]);

  if (!number) return null;

  const changed = tenantId !== (tenants?.length === 1 ? tenants[0]!.id : null);

  const submit = async () => {
    if (tenantId === null) return;
    setFormError(null);
    try {
      await assign.mutateAsync({ id: number.id, tenantId });
      toast.success("Number assigned", `${number.number} → ${tenants?.find((t) => t.id === tenantId)?.name}`);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not assign the number.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>Assign {number.number}</DialogTitle>
          <DialogDescription>
            The tenant gets the number at once, unrouted and disabled, for their admin to route.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {formError && <Callout tone="danger">{formError}</Callout>}
          <Field label="Tenant" htmlFor="assign-tenant" required>
            <Select value={tenantId?.toString() ?? ""} onValueChange={(v) => setTenantId(Number(v))}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a tenant" />
              </SelectTrigger>
              <SelectContent>
                {(tenants ?? []).map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="default"
            loading={assign.isPending}
            disabled={tenantId === null}
            onClick={() => void submit()}
          >
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Refuse a request, with the reason the admin who asked will read. */
function RejectOrderDialog({
  order, open, onOpenChange,
}: {
  order: NumberOrder | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const reject = useRejectOrder();
  const [reason, setReason] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason("");
      setFormError(null);
    }
  }, [open]);

  if (!order) return null;

  const submit = async () => {
    setFormError(null);
    try {
      await reject.mutateAsync({ id: order.id, reason: reason.trim() || null });
      toast.success("Request rejected", `${order.number} is back in the pool`);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not reject the request.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DiscardGuard changed={reason.trim() !== ""} />
        <DialogHeader>
          <DialogTitle>Reject the request for {order.number}?</DialogTitle>
          <DialogDescription>
            {order.tenant_name ?? "The tenant"} asked for it
            {order.note ? ` for "${order.note}"` : ""}. The number returns to the pool.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {formError && <Callout tone="danger">{formError}</Callout>}
          <Field label="Reason" htmlFor="reject-reason" hint="Shown to the admin who asked.">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Wrong area code for a CA line"
              autoComplete="off"
              maxLength={500}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={reject.isPending} onClick={() => void submit()}>
            Reject request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
